import { useContext } from 'react'

import { LDFlagSet } from 'launchdarkly-react-client-sdk'

import { useFlags as useLDFlags } from './launchdarkly-client-sdk.web'
import { PsaBannerFlagType } from '../alerts/PsaBannerFlagType'
import { IS_PRODUCTION } from '../common/constants/constants'
import { CustomerInstanceMap } from '../common/constants/customer'
import { CustomPreferencesExperimentRoundThreeVariation } from '../experiments/customPreferencesExperimentTypes'
import { LDOverrideContext } from '../ldOverride/context'
import { AiInTriageThreeArmVariation } from '../searchForCare/constants'
import { DEFAULT_SMART_SEARCH_CONFIG, SmartSearchConfig } from '../smartSearch/constants'
import { CategoryPriorityConfig, DEFAULT_CATEGORY_PRIORITY } from '../smartSearch/types'
import { DEFAULT_SPECULATIVE_FILTER_THRESHOLDS } from '../wellnessToolkit/constants'
import { SpeculativeSearchThresholds } from '../wellnessToolkit/types'
export interface LDFlags {
  isDisplayLanguageEnabled: boolean
  isAiInTriageEnabledThreeArm: string
  isLanguageSelectorEnabled: boolean
  isSmartlingContextCaptureEnabled: boolean
  showDevLanguages: boolean
  showNewCaseloadDashboardProviders: boolean
  showPlatformEngagementDashboard: boolean
  isMobileBrowserToAppLoginEnabled: boolean
  requireEligibilityConfirmation?: {
    isEnabled: boolean
    skipIneligibleUserWithLyraCode?: boolean
  }
  languagesEnabled: string[]
  micrositeLanguagesEnabled: string[]
  customersToShowClientEAPCheck: { supportedCustomers: string[] }
  isECDCEnabled: boolean
  isGuaranteedTimeSessionTypeEnabled: boolean
  isCaregiverTrainingEnabled: boolean
  isCaregiverTrainingProviderTitlesEnabled: boolean
  isNewRegistrationNomenclatureEnabled: boolean
  useUpdatedCareTeamPhoneNumberPage: boolean
  shouldIncludeRelID: boolean
  shouldShowPastQuartersInBCXDashboards: boolean
  showPastQuartersInLCCDashboard: boolean
  shouldShowCareNavigatorHeaderEntrypoint: boolean
  isUSCountryDisabled: boolean
  isUSTerritoryDeprecationEnabled: boolean
  isLatestMatchesEnabled: boolean
  shouldUseNewRescheduleScreen: boolean
  isMaintenanceModeEnabled: boolean
  isSmartSchedulingOnboardingDisabledForDA: boolean
  searchForCareHeaderExperimentQ12025v2: boolean
  homebaseEssentialsCarouselExperiment: boolean
  isCoachingEntryPointEnabled: boolean
  parentToolKitEntryPointEnabled: boolean
  showInfoNeededWhenUnresolvedInfraction: boolean
  allowDemoBeforeLaunch: boolean
  shouldSendNewWLQ: boolean
  shouldRedirectWLSToCarelon: boolean
  customerInstanceMap: CustomerInstanceMap
  isSubdomainRedirectionEnabled: boolean
  maxBenefitsToShowInExplorer: number
  isClientDataShareEnabled: boolean
  enableMixpanelDebug: boolean
  isPathwaysMicrositeMultiCTAEnabled: boolean
  showMessageProviderAlternateTimesLink: boolean
  isCHCOutage?: boolean
  isCopayForMaxCostEstimateEnabled: boolean
  shouldShowCapacityToAvailabilityRatioAlert: boolean
  showCalendarAvailabilitiesWithCapacity: boolean
  showTemplatesButtonInMessageCenter: boolean
  showDetailedWeeklyViewForCMD: boolean
  showCaseloadFromDropdown: boolean
  enableAIClientIntakeSummaryFetch: boolean
  showAIClientIntakeSummary: boolean
  shouldShowDirectProgramLinkInClientProfile: boolean
  supervisorsThatCanMarkConversationsAsRead: string[]
  isReferralEnabled: boolean
  showNotificationModalOnLoginLandingPage: boolean
  isHPIConsentWithMedicareEnabled: boolean
  showCustomNotifications: boolean
  enableAINotesFeatures: boolean
  enableExportClientRecords: boolean
  enableExportClientRecordsSentryLogging: boolean
  enableFullApiLoggingToSentry: boolean
  enableFullSentryTracing: boolean
  showAINotesFeatures: boolean
  showDevFeatureFlagContainer?: boolean
  isMobileRegistrationEnabled?: boolean
  shouldShowLCPositioning: boolean
  triageScreenersMetadataVersion: string
  isSecondaryHealthPlanCollectionFeatureEnabled?: boolean
  hideUserTestimonialsFromLCxModal: boolean
  isDedicatedCareNavigatorEnabled: boolean
  isFamilyHubEnabled: boolean
  shouldSeePendoGuides: boolean
  shouldShowCostEstimatesAsFullyCovered: boolean
  isCostMessagingEnabled: boolean
  isCustomerDynamicSessionLimitEnabled: boolean
  isPreferredNameEnabled: boolean
  shouldShowNewMinimizedIntegratedBenefitsDesign: boolean
  isElectionsEnabled: boolean
  showPickedForYouV2: boolean
  showEnhancedLCxDiagnosis: boolean
  shouldShowTriageSummary: boolean
  isInProductCalendarEnabled: boolean
  isTimezoneSelectorEnabled: boolean
  triageAnimationsDisabled: boolean
  shouldUseSierraChat: boolean
  shouldSierraChatBeEnabledOutsideOfBusinessHours: boolean
  shouldSierraChatAllowUserAuthentication: boolean
  showPickedForYouV3: boolean
  isWellnessCheckInEnabled: boolean
  routeUserDirectlyToAgentInInAppMessagingChatWindow: boolean
  chatSessionTimeoutDuration: number
  differentialPricingRatesEnabled: boolean
  differentialPricingCalculationEnabled: boolean
  isTimeBasedDABillingEnabled: boolean
  isMicrositeLookAndFeelUpdateEnabled: boolean
  shouldShowCareTabInitial: boolean
  forkedTriageV2Enabled: boolean
  forkedTriageV3Enabled: boolean
  shouldShowAvailabilityPreference: boolean
  isSSLPinningEnabled: boolean
  enableInAppMessagingForTesting: boolean
  isSpeculativeFilterEnabled: boolean
  speculativeSearchThresholds: SpeculativeSearchThresholds
  universalSearchConfig: SmartSearchConfig
  universalSearchCategoryPriority: CategoryPriorityConfig
  renderSaveYourSpotV2Assignment: boolean[]
  shouldShowProviderQuickNavigation: boolean
  adultTriageDisabled: boolean
  forceAdultFlowsToHPI: boolean
  showInProductCalendarAlerts: boolean
  isPhoneNumberRequiredForApptRequest: boolean
  isCookieBannerEnabled: boolean
  inProductCalendarRefreshIntervalMS: number
  isLyraSelectEnabled: boolean
  isWellbeingEnabled: boolean
  showCostTransparency: boolean
  isInProductCalendarAddModalEnabled: boolean
  isDACalendarTransitionEnabled: boolean
  isInProductCalendarModalityEnabled: boolean
  highRiskOUSAppointmentBookingEmailAddress: string
  canSelectMultiplePrimaryNeeds: boolean
  shouldRouteToProgramAwareTriage: boolean
  emailCapturePageExperimentV2: string
  isWellnessCheckInBenefitsEntryPointEnabled: boolean
  requireUpdateNativeAppVersion: boolean
  psaBanner: PsaBannerFlagType | undefined
  showAINotesForLCMAndCLE: boolean
  isHomebaseWellnessCheckInEntrypointEnabled: boolean
  refetchOnConversationAdded: boolean
  shouldOnlySeeCareNavigatorContactScreenInPlatform: boolean
  isFavoritedProvidersEnabled: boolean
  isHomebaseEssentialsEventsEnabled: boolean
  isSideNavigationBarEnabled: boolean
  parentTeenBookingImprovementsEnabled: boolean
  isTeenLedProfileBannerEnabled: boolean
  shouldShowConfirmationPageCapacityChecks: boolean
  isExpandableWorkingHoursEnabled: boolean
  isSelectWellnessToolkitsRound2Enabled: boolean
  isSmartTemplatesEnabled: boolean
  shouldShowMessengerDraftsUtility: boolean
  shouldShowMessengerAIDrafts: boolean
  isAiCoachEnabled: boolean
  isCurrentEpisodeAppointmentsForClientListEnabled: boolean
  isTwilioDeprecatedForLyraAI: boolean
  isAiCoachVoiceEnabled: boolean
  isSavedProviderResultsReengagementEmailsEnabled: boolean
  isGooglePerformanceEnabled: boolean
  isGoogleCatalystLyraAiEnabled: boolean
  isGoogleCatalystHomebaseBannerEnabled: boolean
  isCatalightHomebaseBannerEnabled: boolean
  isMemberSwitchEmployerSelfServeEnabled: boolean
  isTrsSearchLocationDataEnabled: boolean
  isHideCostAndSessionLimitEnabled: boolean
  isHideCoveragePageEnabled: boolean
  shouldShowWalmartHPICostTransparency: boolean
  is1LyraEnabledForHomebase: boolean | undefined
  shouldShowPostSessionAIDrafts: boolean
  showWellnessCenterEssentials: boolean
  isNewBenefitsPageDesignEnabled: boolean
  showParentLedLCTTeenBookingUpdates: boolean
  ppuiPersistedEndpointVersions?: Record<string, number>
  ppuiShouldPersistEndpoints: boolean
  careProgressSummaryUI: boolean
  googleOUSComingSoonMicrositeEnabled: boolean
  isManagerHubEnabled: boolean
  isManagerCoachingEnabled: boolean
  shouldRedirectToComingSoon: boolean
  daMessagingSLADisclaimer: boolean
  isDAProviderMigrated: boolean
  isDAMinorProgramEnabled: boolean
  enableDAFlexibleNote: boolean
  enableDAFlexibleAssessment: boolean
  showMemberRecommendedBadge: boolean
  clientListWithEnabledPreferredName: string[]
  isAddNewClientsEnabled: boolean
  showIPCSessionCreate: boolean
  notesSidePanel: {
    endofsession: boolean
    general: boolean
    initial: boolean
    messagecenter: boolean
    referral: boolean
    session: boolean
  }
  isInProductCalendarModalityEnabledS4C: boolean
  isClientDisabled: boolean
  shouldDisplayHighRiskModalForDirectBooking: boolean
  shouldUseInternationalCoachingSesssionType: boolean
  areExpandedGlobalPreferencesEnabled: boolean
  areConservativeGlobalPreferencesEnabled: boolean
  isPracticeAdminImpersonationEnabled: boolean
  isNeurodiversityCOEEnabled: boolean
  isHealthChallengesToolkitEnabled: boolean
  isWomensHealthToolkitEnabled: boolean
  isCriticalIncidentSupportToolkitEnabled: boolean
  isDisasterResponseServiceEnabled: boolean
  isToolkitDirectoryPageEnabled: boolean
  shouldHideEligibilityTypeQuestion: boolean
  isBenefitsExtensionBannerDisplayed: boolean
  essentialsInitialLoadParamEnabled: boolean
  isHealthPlanOCREnabled: boolean
  isPromotionalSMSEnabled: boolean
  showWalmartNeurodiversityContent: boolean
  isProviderLevelFeedbackEnabled: boolean
  isClientHeaderRedesignEnabled: boolean
  isCustomPreferencesEnabled: boolean
  customPreferencesExperimentRoundThree: CustomPreferencesExperimentRoundThreeVariation
  isPreferencesPrimaryButtonFocusedDesignEnabled: boolean
  showProviderAdjectives: boolean
  isSelfPayEnabled: boolean
  isNotificationCenterRefreshEnabled: boolean
  isStripeTextToPayEnabled: boolean
  enableAirlineAndEAPFeaturesForNotes: boolean
  airlineCustomerValues: string[]
  isUniversalSearchEnabled: boolean
  isUngatedEssentialsEventsEnabled: boolean
  isOnsiteEntrypointsEnabled: boolean
  is9AMHealthIntegrationEnabled: boolean
  shouldPopulateApptPhoneNumber: boolean
  isEapHpiInProductNotificationsEnabled: boolean
  isNeuroPilotEnabled: boolean
  isCareInsightsVisible: boolean
  showRiskManagementInCareInsights: boolean
  isLCTTeensAdditionalRemindersEnabled: boolean
  isRecordingConsentBannerEnabled: boolean
  isFiveMinuteVideoSessionWarningEnabled: boolean
  isBackdatedAppointmentsEnabled: boolean
  AllowDAEngageProvidersToBookInactiveClients: boolean
  isLegacyProviderScheduleSessionEnabled: boolean
  isSessionsCostEstimateEnabled: boolean
  isGooglePlacesAPIEnabled: boolean
  isDADashboardEnabled: boolean
  isFreshpaintEnabled: boolean
  isConnectAtGlanceChartsDisabled: boolean
  isConnectAIInsightsDisabled: boolean
  isConnectCriticalIncidentHubEnabled: boolean
  isConnectSigmaMigrationPhaseTwoEnabled: boolean
  isConnectTableauCISDisabled: boolean
  isMHETabEnabled: boolean
  connectDataCollectionDates: {
    hpi?: { startDate: string; endDate: string }
    yearEndReview?: { startDate: string; endDate: string }
  }
  isOnsiteEligibleResponseEnabled: boolean
  isDbtReferralEnabled: boolean
  isDbtReferralInsuranceCardUploadEnabled: boolean
  isDBTTeensEnabled: boolean
  isMultipleOnsiteIPCLocationEnabled: boolean
  isDAEngageOnOffPlatformVideoEnabled: boolean
  isRegistrationAndLoginDisabled: boolean
  showCostSharingAccountPaymentMethod: boolean
  showLyraSelectStateOfEmergencyBanner: boolean
  isBendProviderUsingCSForAvailability: boolean
  isDocumentMessageEnabled: boolean
  is1LyraEnabledForDirectBookingLinks: boolean
  accountMigrationBookingLinkExpiration: string
  isInactivityCountdownDebugEnabled: boolean
  isBrazeEnabled: boolean
  isLyraAIDemoProvider: boolean
  globalHeaderConfig: { web: string[]; native: string[] }
  isZendeskWidgetEnabled: boolean
  isEnhancedZeroProviderResultsEnabled: boolean
  isCarelonOutpatientTherapyEnabled: boolean
  isUserPatchEnabled: boolean
  oct2025ToolkitsEnabled: boolean
  nov2025ToolkitsEnabled: boolean
  isConnectSideNavV2Enabled: boolean
  isNPTestingEnabled: boolean
  shouldSurfaceNPEvalHPIScreen: boolean
  npTestingPlans: boolean
  /**
   * [ENGBEND-12942] Dev-only escape hatch: neuropsych episodes cannot be created yet
   * (ENGBEND-12640), so there is no way to reach the Evaluation tab with real data. Ignored in
   * production; delete it once real NP episodes exist.
   */
  npTestingPlansDevOverride?: {
    forceEpisode?: boolean
    /** Pins the intake-note gate open or shut. Omit to read the real notes. */
    intakeNoteCompleted?: boolean
    forceRole?: boolean
  }
  isPersonifyAttachExternalIdEnabled: boolean
}

export const INITIAL_LD_FLAGS: LDFlags = {
  shouldOnlySeeCareNavigatorContactScreenInPlatform: false,
  shouldShowCapacityToAvailabilityRatioAlert: false,
  showInfoNeededWhenUnresolvedInfraction: false,
  allowDemoBeforeLaunch: false,
  shouldSendNewWLQ: false,
  shouldRedirectWLSToCarelon: false,
  isClientDataShareEnabled: false,
  enableMixpanelDebug: false,
  isPathwaysMicrositeMultiCTAEnabled: false,
  showMessageProviderAlternateTimesLink: false,
  showCalendarAvailabilitiesWithCapacity: false,
  showTemplatesButtonInMessageCenter: false,
  showDetailedWeeklyViewForCMD: false,
  showCaseloadFromDropdown: false,
  enableAIClientIntakeSummaryFetch: false,
  enableExportClientRecords: false,
  enableExportClientRecordsSentryLogging: false,
  enableFullApiLoggingToSentry: false,
  enableFullSentryTracing: false,
  showAIClientIntakeSummary: false,
  shouldShowDirectProgramLinkInClientProfile: false,
  supervisorsThatCanMarkConversationsAsRead: [],
  isReferralEnabled: false,
  enableAINotesFeatures: false,
  showAINotesFeatures: false,
  showDevFeatureFlagContainer: false,
  isDisplayLanguageEnabled: true,
  isAiInTriageEnabledThreeArm: AiInTriageThreeArmVariation.CONTROL,
  isMobileBrowserToAppLoginEnabled: false,
  showCustomNotifications: false,
  languagesEnabled: [],
  isUSCountryDisabled: false,
  isUSTerritoryDeprecationEnabled: false,
  isLatestMatchesEnabled: false,
  isMobileRegistrationEnabled: false,
  requireEligibilityConfirmation: { isEnabled: false },
  isCoachingEntryPointEnabled: false,
  parentToolKitEntryPointEnabled: false,
  customerInstanceMap: {},
  isSubdomainRedirectionEnabled: false,
  maxBenefitsToShowInExplorer: 3,
  isCHCOutage: false,
  isCopayForMaxCostEstimateEnabled: false,
  isHPIConsentWithMedicareEnabled: false,
  isLanguageSelectorEnabled: false,
  isSmartlingContextCaptureEnabled: false,
  showDevLanguages: false,
  showNewCaseloadDashboardProviders: false,
  showPlatformEngagementDashboard: false,
  micrositeLanguagesEnabled: [],
  customersToShowClientEAPCheck: { supportedCustomers: [] },
  isECDCEnabled: false,
  isGuaranteedTimeSessionTypeEnabled: true,
  isCaregiverTrainingEnabled: false,
  isCaregiverTrainingProviderTitlesEnabled: false,
  isNewRegistrationNomenclatureEnabled: false,
  useUpdatedCareTeamPhoneNumberPage: false,
  shouldIncludeRelID: false,
  shouldShowPastQuartersInBCXDashboards: false,
  showPastQuartersInLCCDashboard: false,
  shouldShowCareNavigatorHeaderEntrypoint: false,
  shouldUseNewRescheduleScreen: false,
  isSmartSchedulingOnboardingDisabledForDA: false,
  isMaintenanceModeEnabled: false,
  showNotificationModalOnLoginLandingPage: false,
  searchForCareHeaderExperimentQ12025v2: false,
  homebaseEssentialsCarouselExperiment: false,
  shouldShowLCPositioning: false,
  triageScreenersMetadataVersion: 'latest',
  isSecondaryHealthPlanCollectionFeatureEnabled: false,
  hideUserTestimonialsFromLCxModal: false,
  isDedicatedCareNavigatorEnabled: false,
  isFamilyHubEnabled: false,
  shouldSeePendoGuides: false,
  shouldShowCostEstimatesAsFullyCovered: false,
  isCostMessagingEnabled: false,
  isCustomerDynamicSessionLimitEnabled: false,
  isPreferredNameEnabled: false,
  shouldShowNewMinimizedIntegratedBenefitsDesign: false,
  isElectionsEnabled: false,
  showPickedForYouV2: false,
  showEnhancedLCxDiagnosis: false,
  shouldShowTriageSummary: false,
  isInProductCalendarEnabled: false,
  isTimezoneSelectorEnabled: false,
  triageAnimationsDisabled: false,
  shouldUseSierraChat: false,
  shouldSierraChatBeEnabledOutsideOfBusinessHours: false,
  shouldSierraChatAllowUserAuthentication: false,
  showPickedForYouV3: false,
  isWellnessCheckInEnabled: false,
  routeUserDirectlyToAgentInInAppMessagingChatWindow: false,
  chatSessionTimeoutDuration: 600,
  differentialPricingRatesEnabled: false,
  differentialPricingCalculationEnabled: false,
  isTimeBasedDABillingEnabled: false,
  isMicrositeLookAndFeelUpdateEnabled: false,
  shouldShowCareTabInitial: false,
  forkedTriageV2Enabled: false,
  forkedTriageV3Enabled: false,
  shouldShowAvailabilityPreference: false,
  enableInAppMessagingForTesting: false,
  renderSaveYourSpotV2Assignment: [],
  isSpeculativeFilterEnabled: false,
  speculativeSearchThresholds: DEFAULT_SPECULATIVE_FILTER_THRESHOLDS,
  universalSearchConfig: DEFAULT_SMART_SEARCH_CONFIG,
  universalSearchCategoryPriority: DEFAULT_CATEGORY_PRIORITY,
  isSSLPinningEnabled: false,
  shouldShowProviderQuickNavigation: false,
  adultTriageDisabled: false,
  forceAdultFlowsToHPI: false,
  showInProductCalendarAlerts: false,
  isPhoneNumberRequiredForApptRequest: false,
  isCookieBannerEnabled: false,
  inProductCalendarRefreshIntervalMS: 300000,
  isLyraSelectEnabled: false,
  isWellbeingEnabled: false,
  showCostTransparency: false,
  isInProductCalendarAddModalEnabled: false,
  isDACalendarTransitionEnabled: false,
  isInProductCalendarModalityEnabled: false,
  highRiskOUSAppointmentBookingEmailAddress: '',
  canSelectMultiplePrimaryNeeds: false,
  shouldRouteToProgramAwareTriage: false,
  emailCapturePageExperimentV2: 'Control', // Group that is not in the experiment
  isWellnessCheckInBenefitsEntryPointEnabled: false,
  requireUpdateNativeAppVersion: false,
  psaBanner: {
    enabled: false,
  },
  showAINotesForLCMAndCLE: false,
  isHomebaseWellnessCheckInEntrypointEnabled: false,
  refetchOnConversationAdded: false,
  isFavoritedProvidersEnabled: false,
  isHomebaseEssentialsEventsEnabled: false,
  isSideNavigationBarEnabled: false,
  parentTeenBookingImprovementsEnabled: false,
  isTeenLedProfileBannerEnabled: false,
  shouldShowConfirmationPageCapacityChecks: false,
  isExpandableWorkingHoursEnabled: false,
  isSelectWellnessToolkitsRound2Enabled: false,
  isSmartTemplatesEnabled: false,
  shouldShowMessengerDraftsUtility: false,
  shouldShowMessengerAIDrafts: false,
  isAiCoachEnabled: false,
  isCurrentEpisodeAppointmentsForClientListEnabled: true,
  isTwilioDeprecatedForLyraAI: false,
  isAiCoachVoiceEnabled: false,
  isSavedProviderResultsReengagementEmailsEnabled: false,
  is1LyraEnabledForHomebase: undefined,
  isGooglePerformanceEnabled: false,
  isGoogleCatalystLyraAiEnabled: false,
  isGoogleCatalystHomebaseBannerEnabled: false,
  isCatalightHomebaseBannerEnabled: false,
  isMemberSwitchEmployerSelfServeEnabled: false,
  isTrsSearchLocationDataEnabled: false,
  isHideCostAndSessionLimitEnabled: false,
  isHideCoveragePageEnabled: false,
  shouldShowWalmartHPICostTransparency: false,
  shouldShowPostSessionAIDrafts: false,
  showWellnessCenterEssentials: false,
  isNewBenefitsPageDesignEnabled: false,
  showParentLedLCTTeenBookingUpdates: false,
  ppuiPersistedEndpointVersions: undefined,
  ppuiShouldPersistEndpoints: false,
  careProgressSummaryUI: false,
  googleOUSComingSoonMicrositeEnabled: false,
  isManagerHubEnabled: false,
  isManagerCoachingEnabled: false,
  shouldRedirectToComingSoon: false,
  daMessagingSLADisclaimer: false,
  isDAProviderMigrated: false,
  isDAMinorProgramEnabled: false,
  enableDAFlexibleNote: false,
  enableDAFlexibleAssessment: false,
  showMemberRecommendedBadge: false,
  clientListWithEnabledPreferredName: [],
  isAddNewClientsEnabled: false,
  showIPCSessionCreate: false,
  notesSidePanel: {
    endofsession: false,
    general: false,
    initial: false,
    messagecenter: false,
    referral: false,
    session: false,
  },
  isInProductCalendarModalityEnabledS4C: false,
  isClientDisabled: false,
  shouldDisplayHighRiskModalForDirectBooking: false,
  shouldUseInternationalCoachingSesssionType: false,
  areExpandedGlobalPreferencesEnabled: false,
  areConservativeGlobalPreferencesEnabled: false,
  isPracticeAdminImpersonationEnabled: false,
  isNeurodiversityCOEEnabled: false,
  isHealthChallengesToolkitEnabled: false,
  isWomensHealthToolkitEnabled: false,
  isCriticalIncidentSupportToolkitEnabled: false,
  isDisasterResponseServiceEnabled: false,
  isToolkitDirectoryPageEnabled: false,
  shouldHideEligibilityTypeQuestion: false,
  isBenefitsExtensionBannerDisplayed: false,
  essentialsInitialLoadParamEnabled: false,
  isHealthPlanOCREnabled: false,
  isPromotionalSMSEnabled: false,
  showWalmartNeurodiversityContent: false,
  isProviderLevelFeedbackEnabled: false,
  isClientHeaderRedesignEnabled: false,
  isCustomPreferencesEnabled: false,
  customPreferencesExperimentRoundThree: CustomPreferencesExperimentRoundThreeVariation.CONTROL,
  isPreferencesPrimaryButtonFocusedDesignEnabled: false,
  showProviderAdjectives: false,
  isSelfPayEnabled: false,
  isNotificationCenterRefreshEnabled: false,
  isStripeTextToPayEnabled: false,
  enableAirlineAndEAPFeaturesForNotes: false,
  airlineCustomerValues: [],
  isUniversalSearchEnabled: false,
  isUngatedEssentialsEventsEnabled: false,
  isOnsiteEntrypointsEnabled: false,
  is9AMHealthIntegrationEnabled: false,
  shouldPopulateApptPhoneNumber: false,
  isEapHpiInProductNotificationsEnabled: false,
  isNeuroPilotEnabled: false,
  isCareInsightsVisible: false,
  showRiskManagementInCareInsights: false,
  isLCTTeensAdditionalRemindersEnabled: false,
  isRecordingConsentBannerEnabled: false,
  isFiveMinuteVideoSessionWarningEnabled: false,
  isBackdatedAppointmentsEnabled: false,
  AllowDAEngageProvidersToBookInactiveClients: false,
  isLegacyProviderScheduleSessionEnabled: false,
  isSessionsCostEstimateEnabled: false,
  isGooglePlacesAPIEnabled: false,
  isDADashboardEnabled: false,
  isFreshpaintEnabled: false,
  isConnectAtGlanceChartsDisabled: false,
  isConnectAIInsightsDisabled: false,
  isConnectCriticalIncidentHubEnabled: false,
  isConnectSigmaMigrationPhaseTwoEnabled: false,
  isConnectTableauCISDisabled: false,
  isMHETabEnabled: false,
  connectDataCollectionDates: {},
  isOnsiteEligibleResponseEnabled: false,
  isDbtReferralEnabled: false,
  isDbtReferralInsuranceCardUploadEnabled: false,
  isDBTTeensEnabled: false,
  isMultipleOnsiteIPCLocationEnabled: false,
  isDAEngageOnOffPlatformVideoEnabled: false,
  isRegistrationAndLoginDisabled: false,
  showCostSharingAccountPaymentMethod: false,
  showLyraSelectStateOfEmergencyBanner: false,
  isBendProviderUsingCSForAvailability: false,
  isDocumentMessageEnabled: false,
  is1LyraEnabledForDirectBookingLinks: false,
  accountMigrationBookingLinkExpiration: '',
  isInactivityCountdownDebugEnabled: false,
  isBrazeEnabled: false,
  isLyraAIDemoProvider: false,
  globalHeaderConfig: { web: [], native: [] },
  isZendeskWidgetEnabled: false,
  isEnhancedZeroProviderResultsEnabled: false,
  isCarelonOutpatientTherapyEnabled: false,
  isUserPatchEnabled: false,
  oct2025ToolkitsEnabled: false,
  nov2025ToolkitsEnabled: false,
  isConnectSideNavV2Enabled: false,
  isNPTestingEnabled: false,
  shouldSurfaceNPEvalHPIScreen: false,
  npTestingPlans: false,
  npTestingPlansDevOverride: undefined,
  isPersonifyAttachExternalIdEnabled: false,
}

export const FLAGS_PARAM_NAME = 'flags'

function isDevFlagsEnabled() {
  // Allow dev flag overrides in __DEV__ builds and any non-production environments
  return (typeof __DEV__ !== 'undefined' && __DEV__) || !IS_PRODUCTION
}

/**
 * Get flags from dev hooks:
 *  1. flags set using localstorage
 *  2. flags set using URL param
 */
export function getDevFlags(): Partial<LDFlags> {
  if (!isDevFlagsEnabled()) {
    return {}
  }

  const devFlags = []

  // try to get flags from localstorage
  if (typeof localStorage !== 'undefined' && localStorage.getItem(FLAGS_PARAM_NAME)) {
    devFlags.push(localStorage.getItem(FLAGS_PARAM_NAME))
  }

  // try to get flags from URL param
  const searchParams = new URLSearchParams(window?.location?.search)
  if (searchParams.has(FLAGS_PARAM_NAME)) {
    devFlags.push(searchParams.get(FLAGS_PARAM_NAME))
  }

  let result = {}
  try {
    // merge dev flags with original LD flags from LD server
    result = devFlags.reduce((acc, flagsStr) => {
      const flags = flagsStr ? JSON.parse(flagsStr) : {}
      return { ...acc, ...flags }
    }, {})
  } catch (e) {
    console.error('Error parsing flags param. Make sure flags has a valid JSON string.', e)
  }

  return result
}

/**
 * Dev hook to overwrite flags set on LD server.
 * They are overwritten in the following order:
 *  1. flags set using localstorage
 *  2. flags set using URL param
 *
 * @param ldFlags flags on LD server
 */
export function overrideFlagsWithSearchParams(ldFlags: LDFlags, overrideFlags: LDFlagSet): LDFlags {
  const devFlags = { ...getDevFlags(), ...overrideFlags }
  // `ldFlags` is a Proxy object that has a get trap that triggers an evaluation event when a flag is being read.
  // Here in order to read the devFlags first, we create a another Proxy object around it.
  // We can't simply return { ...ldFlags, ...devFlags } neither use Object.assign here because that causes
  // the Proxy get trap to be called for all flags, triggering an evaluation event for all flags,
  // which blows up the batch events queue and causes events to be dropped.
  return new Proxy(ldFlags, {
    get(target, prop, receiver) {
      return devFlags[prop as keyof LDFlags] ?? Reflect.get(target, prop, receiver)
    },
  })
}

const useFlagsDev = (): LDFlags => {
  const ldFlags = useLDFlags() as LDFlags
  const { overrideFlags } = useContext(LDOverrideContext)
  return overrideFlagsWithSearchParams(ldFlags, overrideFlags)
}

export function useFlags(): LDFlags {
  return (isDevFlagsEnabled() ? useFlagsDev : (useLDFlags as () => LDFlags))()
}

export const defaultFlags: Partial<LDFlags> = {
  speculativeSearchThresholds: DEFAULT_SPECULATIVE_FILTER_THRESHOLDS,
  universalSearchConfig: DEFAULT_SMART_SEARCH_CONFIG,
  universalSearchCategoryPriority: DEFAULT_CATEGORY_PRIORITY,
  isPhoneNumberRequiredForApptRequest: true,
  renderSaveYourSpotV2Assignment: [],
  psaBanner: {
    enabled: false,
  },
  showMemberRecommendedBadge: false,
  isCurrentEpisodeAppointmentsForClientListEnabled: true,
  connectDataCollectionDates: {},
}
